import { repository, session } from "clientInstance";
import { Permission } from "client/resources/permission";
import { OctopusError, ProjectResource, ReleaseResource } from "client/resources";
import { isAllowed } from "components/PermissionCheck/PermissionCheck";

interface TaskStatus {
    IsEnabled: boolean;
    IsComplete: boolean;
}

interface Task extends TaskStatus {
    Percent: number;
}

export interface Tasks {
    Infrastructure: Task & { CreatedEnvironment: TaskStatus, CreatedMachine: TaskStatus, AddedAzureAccount: TaskStatus };
    Package: Task & { UploadedPackage: TaskStatus, AddedFeed: TaskStatus };
    Project: Task & { CreatedProject: TaskStatus, AddedProjectStep: TaskStatus };
    Deploy: Task & { CreatedRelease: TaskStatus, DeployedRelease: TaskStatus };
}

export class GettingStartedStatus {
    private static subscriberId: number = 0;

    tasks: Tasks;
    next = "";
    projects: ProjectResource[] = [];
    releases: ReleaseResource[] = [];

    private subscribers: {
        [subscriberId: string]: (status: GettingStartedStatus) => void
    } = {};

    private readonly refreshPeriod: number = 2000;

    constructor() {
        this.refresh = this.refresh.bind(this);
        this.init = this.init.bind(this);
        this.subscribe = this.subscribe.bind(this);
        this.unsubscribe = this.unsubscribe.bind(this);
        this.populateReleasesList = this.populateReleasesList.bind(this);
        this.populateProjectsList = this.populateProjectsList.bind(this);
    }

    init() {
        return this.refresh();
    }

    subscribe(callback: (status: GettingStartedStatus) => void) {
        const subscriberId = ++GettingStartedStatus.subscriberId;
        this.subscribers[subscriberId] = callback;

        if (Object.getOwnPropertyNames(this.subscribers).length === 1) {
            this.refresh();
        }

        return subscriberId;
    }

    unsubscribe(subscriberId: number) {
        delete this.subscribers[subscriberId];
    }

    private async populateReleasesList() {
        if (isAllowed({permission: Permission.ReleaseView, wildcard: true})) {
            try {
                return (await repository.Releases.list()).Items;
            } catch (ex) {
                if (ex instanceof OctopusError && ex.StatusCode !== 403) {
                    throw ex;
                }
            }
        }

        return [];
    }

    private async populateProjectsList() {
        if (isAllowed({permission: Permission.ProjectView, wildcard: true})) {
            try {
                return await repository.Projects.all();
            } catch (ex) {
                if (ex instanceof OctopusError && ex.StatusCode !== 403) {
                    throw ex;
                }
            }
        }

        return [];
    }

    private async refresh() {
        if (this.tasks && document.hidden) {
            setTimeout(() => this.refresh(), this.refreshPeriod);
            return;
        }

        const userOnboardingData = await repository.UserOnboarding.get();
        let next = "";

        this.tasks = { Infrastructure: null, Deploy: null, Package: null, Project: null };

        for (const task of userOnboardingData.Tasks) {
            (this.tasks as any)[task.Category] = (this.tasks as any)[task.Category] || {};
            (this.tasks as any)[task.Category][task.Name] = (this.tasks as any)[task.Category][task.Name] || {};
            (this.tasks as any)[task.Category][task.Name].IsComplete = task.IsComplete;
            (this.tasks as any)[task.Category][task.Name].IsEnabled = task.IsEnabled;
        }

        this.tasks.Infrastructure.IsEnabled = this.tasks.Infrastructure.CreatedEnvironment.IsEnabled;
        this.tasks.Infrastructure.Percent = 0;
        this.tasks.Infrastructure.IsComplete = false;

        if (this.tasks.Infrastructure.CreatedEnvironment.IsComplete) {
            this.tasks.Infrastructure.Percent += 50;
        } else if (!!!next) {
            next = "Infrastructure.CreatedEnvironment";
        }
        if (this.tasks.Infrastructure.CreatedMachine.IsComplete) {
            this.tasks.Infrastructure.Percent += 50;
        } else if (this.tasks.Infrastructure.AddedAzureAccount.IsComplete) {
            this.tasks.Infrastructure.Percent += 50;
        } else if (!!!next) {
            next = "Infrastructure.CreatedMachineOrAccount";
        }
        if (this.tasks.Infrastructure.Percent === 100) {
            this.tasks.Infrastructure.IsComplete = true;
        }

        this.tasks.Package.IsEnabled = this.tasks.Package.UploadedPackage.IsEnabled;
        this.tasks.Package.Percent = 0;
        this.tasks.Package.IsComplete = false;

        if (this.tasks.Package.UploadedPackage.IsComplete) {
            this.tasks.Package.Percent += 100;
        } else if (this.tasks.Package.AddedFeed.IsComplete) {
            this.tasks.Package.Percent += 100;
        } else if (!!!next) {
            // tslint:disable-next-line
            if (this.tasks.Deploy.CreatedRelease.IsEnabled && !this.tasks.Deploy.CreatedRelease.IsComplete) {
                next = "Deploy.CreatedRelease";
            } else if (this.tasks.Deploy.DeployedRelease.IsEnabled && !this.tasks.Deploy.DeployedRelease.IsComplete) {
                next = "Deploy.DeployedRelease";
            } else {
                next = "Package";
            }
        }
        if (this.tasks.Package.Percent === 100) {
            this.tasks.Package.IsComplete = true;
        }

        this.tasks.Project.IsEnabled = this.tasks.Project.CreatedProject.IsEnabled;
        this.tasks.Project.Percent = 0;
        this.tasks.Project.IsComplete = false;

        if (this.tasks.Project.CreatedProject.IsComplete) {
            this.tasks.Project.Percent += 50;
        } else if (!!!next) {
            next = "Project.CreatedProject";
        }
        if (this.tasks.Project.AddedProjectStep.IsComplete) {
            this.tasks.Project.Percent += 50;
        } else if (!!!next) {
            next = "Project.AddedProjectStep";
        }
        if (this.tasks.Project.Percent === 100) {
            this.tasks.Project.IsComplete = true;
        }

        this.tasks.Deploy.IsEnabled = this.tasks.Deploy.CreatedRelease.IsEnabled;
        this.tasks.Deploy.Percent = 0;
        this.tasks.Deploy.IsComplete = false;

        if (this.tasks.Deploy.CreatedRelease.IsComplete) {
            this.tasks.Deploy.Percent += 50;
        } else if (!!!next) {
            next = "Deploy.CreatedRelease";
        }
        if (this.tasks.Deploy.DeployedRelease.IsComplete) {
            this.tasks.Deploy.Percent += 50;
        } else if (!!!next) {
            next = "Deploy.DeployedRelease";
        }
        if (this.tasks.Deploy.Percent === 100) {
            this.tasks.Deploy.IsComplete = true;
        }

        this.next = next;

        const [projects, releases] = await Promise.all([this.populateProjectsList(), this.populateReleasesList()]);

        this.projects = projects;
        this.releases = releases.filter(r => projects.some(p => p.Id === r.ProjectId)); //Ensure we only display releases that we have projects for

        const subscriberIds = Object.getOwnPropertyNames(this.subscribers);
        subscriberIds.forEach((s, _, subscribers) => {
            try {
                this.subscribers[s](this);
            } catch (ex) {
                //Ignore on purpose
            }
        }, this);
        if (subscriberIds.length > 0 && !this.tasks.Deploy.DeployedRelease.IsComplete) {
            setTimeout(() => this.refresh(), this.refreshPeriod);
        }
    }
}

const gettingStartedStatusInstance = new GettingStartedStatus();

const gettingStartedStatus = {
    subscribe(callback: (status: GettingStartedStatus) => void) {
        const id = gettingStartedStatusInstance.subscribe(callback);

        return () => {
            gettingStartedStatusInstance.unsubscribe(id);
        };
    },
    status: async () => {
        await gettingStartedStatusInstance.init();
        return gettingStartedStatusInstance;
    }
};

export { gettingStartedStatus };


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/GettingStarted/gettingStartedStatus.ts